import { io, Socket } from "socket.io-client";
import { SOCKET_SERVER_URL } from "../constants/constants";
import { RoundInfo } from "../interface/SocketInterface";
import path from "path";


export class ClientSocket {
    socket: Socket | null;
    private _isConnected = false;

    constructor() {
        this.socket = null;
    }

    public get isConnected(): boolean {
        return this._isConnected && this.socket?.connected === true;
    }

    public get parseBaseUrl(): {path:string|undefined, base:string}|undefined {
        try {
            const url = new URL(SOCKET_SERVER_URL);

            return {
                base: `${url.protocol}//${url.host.replace('//', '/')}`,
                path: !!url.pathname && url.pathname != '/' ?  (`${url.pathname}/socket.io/`).replace('//', '/') : undefined
            }

        } catch (error) {
            console.error(`[Frontend WebSocket] ERROR parsing SOCKET_SERVER_URL:`, error);
            return {
                base: '',
                path: undefined
            }
        }
    }

    public connect(): Promise<void> {
        return new Promise((resolve, reject) => {
            console.log(`[Frontend WebSocket] Attempting to connect to: ${SOCKET_SERVER_URL}`);

            const baseConfig = this.parseBaseUrl;
            console.log("", baseConfig);

            console.log(`[Frontend WebSocket] Base URL: ${baseConfig}`);
            console.log(`[Frontend WebSocket] Socket Path: ${baseConfig?.path}`);
            
            this.socket = io(this.parseBaseUrl?.base, {
                path: this.parseBaseUrl?.path,
       
            });
            
            this.socket.on('connect', () => {
                console.log(`[Frontend WebSocket] Successfully connected to ${SOCKET_SERVER_URL}`);
                console.log(`[Frontend WebSocket] Socket ID: ${this.socket?.id}`);
                this._isConnected = true;
                resolve();
            });

            this.socket.on('connect_error', (error) => {
                console.error(`[Frontend WebSocket] Connection error: ${error.message}`);
                this._isConnected = false;
                reject(error);
            });

            this.socket.on('disconnect', (reason) => {
                console.log(`[Frontend WebSocket] Disconnected. Reason: ${reason}`);
                this._isConnected = false;
            });

            this.socket.on('simulation_error', (error) => {
                console.error(`[Frontend WebSocket] Simulation error received: ${JSON.stringify(error)}`);
            });

            this.socket.connect();
        });
    }

    public disconnect() {
        console.log(`[Frontend WebSocket] Disconnecting from server`);
        this._isConnected = false;
        this.socket?.disconnect();
        this.socket = null;
    }

    public bindFn(onSimulationFn: (values: any) => void) {
        console.log(`[Frontend WebSocket] ========== BINDING SIMULATION LISTENERS ==========`);
        console.log(`[Frontend WebSocket] Socket exists:`, !!this.socket);
        console.log(`[Frontend WebSocket] Socket connected:`, this.isConnected);
        console.log(`[Frontend WebSocket] Socket ID:`, this.socket?.id);
        
        this.socket?.removeListener("simulation_response");
        this.socket?.removeListener("simulation_error");
        
        this.socket?.on("simulation_response", (values) => {
            console.log(`[Frontend WebSocket] ========== SIMULATION RESPONSE RECEIVED ==========`);
            console.log(`[Frontend WebSocket] Raw response:`, values);
            console.log(`[Frontend WebSocket] Response data length: ${values?.length || 'undefined'}`);
            console.log(`[Frontend WebSocket] Response data type:`, typeof values);
            console.log(`[Frontend WebSocket] Response data structure:`, values ? Object.keys(values[0] || {}) : 'no data');
            
            if (!values || !Array.isArray(values) || values.length === 0) {
                console.error(`[Frontend WebSocket] ERROR: Invalid simulation response data`);
                document.body.style.cursor = "default";
                alert('Simulation failed: No response data received. Please try again.');
                return;
            }
            
            console.log(`[Frontend WebSocket] Calling onSimulationFn with valid data`);
            onSimulationFn(values);
        });
        
        this.socket?.on("simulation_error", (error) => {
            console.error(`[Frontend WebSocket] ========== SIMULATION ERROR RECEIVED ==========`);
            console.error(`[Frontend WebSocket] Error details:`, error);
            document.body.style.cursor = "default";
            alert(`Simulation failed: ${error.error || 'Unknown error'}. Please try again.`);
        });
        
        // Add a listener for debugging all events
        this.socket?.onAny((eventName, ...args) => {
            if (eventName !== 'connect' && eventName !== 'disconnect') {
                console.log(`[Frontend WebSocket] Event received: ${eventName}`, args);
            }
        });
        
        console.log(`[Frontend WebSocket] All simulation listeners bound successfully`);
    }

    public setDisconnectFn(onDisconnect: () => void) {
        this.socket?.removeListener("disconnect");
        this.socket?.on("disconnect", () => {
            onDisconnect();
        });
    }

    public reset_cost_ges() {
        console.log(`[Frontend WebSocket] Emitting reset_cost_ges event`);
        console.log(`[Frontend WebSocket] Socket connected: ${this.isConnected}`);
        
        if (!this.isConnected) {
            console.error(`[Frontend WebSocket] ERROR: Socket not connected! Cannot emit reset_cost_ges`);
            return;
        }
        
        this.socket?.emit("reset_cost_ges", {})
    }
    

    public emitData(data: any[]) {
        console.log(`[Frontend WebSocket] ========== EMITTING SIMULATION STEP ==========`);
        console.log(`[Frontend WebSocket] Socket connected: ${this.isConnected}`);
        console.log(`[Frontend WebSocket] Socket ID: ${this.socket?.id}`);
        console.log(`[Frontend WebSocket] Data being sent:`, data);
        console.log(`[Frontend WebSocket] Data length: ${data?.length || 'undefined'}`);
        console.log(`[Frontend WebSocket] Sample data item:`, data[0]);
        console.log(`[Frontend WebSocket] Data structure keys:`, data[0] ? Object.keys(data[0]) : 'no data');
        
        if (!this.isConnected) {
            console.error(`[Frontend WebSocket] ERROR: Socket not connected! Cannot emit simulation_step`);
            document.body.style.cursor = "default";
            return;
        }
        
        console.log(`[Frontend WebSocket] About to emit 'simulation_step' event...`);
        this.socket?.emit("simulation_step", data);
        console.log(`[Frontend WebSocket] simulation_step event emitted successfully - waiting for response...`);
        
        // Set a timeout to check if we get a response within 10 seconds
        setTimeout(() => {
            console.warn(`[Frontend WebSocket] WARNING: No simulation_response received after 10 seconds`);
            console.warn(`[Frontend WebSocket] This might indicate a problem with the Python backend`);
        }, 10000);
    }

    public onNewRound(roundInfo: RoundInfo, data: any) {
        console.log(`[Frontend WebSocket] Emitting new_round event`);
        console.log(`[Frontend WebSocket] Socket connected: ${this.isConnected}`);
        console.log(`[Frontend WebSocket] Socket ID: ${this.socket?.id}`);
        console.log(`[Frontend WebSocket] Round info:`, roundInfo);
        
        if (!this.isConnected) {
            console.error(`[Frontend WebSocket] ERROR: Socket not connected! Cannot emit new_round`);
            return;
        }
        
        this.socket?.emit("new_round", {...roundInfo, data: { "electric_task": 0, ...data }});
        console.log(`[Frontend WebSocket] new_round event emitted successfully`);
    }
}
